/**
 * attachScrollDashes — custom dash-based scroll indicator (same look as the vault
 * SidePanel).
 *
 * Liegt in platform/shared, weil ihn ausser Study auch die Admin-Konsole benutzt
 * (platform/shared/admin-konsole.js). Die Klassennamen `scroll-dashes`/`scroll-dash`
 * bringt jeder Aufrufer selbst zum Aussehen — Study ueber seine CSS-Variablen, die
 * Konsole ueber eigene, auf `.mednos-konsole` eingegrenzte Regeln.
 *
 * Ursprung: a vertical column of dashes whose count scales with the scrollable
 * overflow, the dash at the current scroll position lights up, and clicking a dash
 * jumps there. Replaces the native scrollbar.
 *
 * @param {HTMLElement} scrollEl  the scrollable element (overflow-y: auto)
 * @param {HTMLElement} dashesEl  an (absolutely positioned) `.scroll-dashes` container
 * @param {{pxJeStrich?: number, sockel?: number, max?: number}} [opts]
 *   Dichte der Striche. Ohne Angabe wie bisher (ein Strich je 40px verborgenen
 *   Inhalts, plus 4, hoechstens 30). Papers' Buddy-Chat will weniger, dafuer
 *   groessere Striche (27.09.2026).
 * @returns {Function} a detach function that removes the listeners
 */
export function attachScrollDashes(scrollEl, dashesEl, opts = {}) {
    if (!scrollEl || !dashesEl) return () => {};
    let dashCount = 0;
    const pxJeStrich = opts.pxJeStrich ?? 40;
    const sockel = opts.sockel ?? 4;
    const maxStriche = opts.max ?? 30;

    const update = () => {
        const overflow = scrollEl.scrollHeight - scrollEl.clientHeight;
        // Not scrollable → no dashes.
        if (overflow <= 2 || scrollEl.clientHeight === 0) {
            scrollEl.classList.remove('has-scroll');
            if (dashCount !== 0) { dashesEl.innerHTML = ''; dashCount = 0; }
            return;
        }
        scrollEl.classList.add('has-scroll');

        // One dash per ~40px of hidden content, but capped to however many dashes
        // fit the indicator column at its CSS gap — with a fixed gap the dashes
        // would otherwise overflow the available height on small viewports / long
        // content. DASH_H matches the .scroll-dash height; the gap is read from
        // the dashes container so each consumer's own `gap` is respected.
        const DASH_H = 2;
        const gapPx = parseFloat(getComputedStyle(dashesEl).rowGap) || 6;
        const avail = scrollEl.clientHeight;
        const maxDashes = Math.max(2, Math.floor((avail - 8 + gapPx) / (DASH_H + gapPx)));
        const n = Math.max(2, Math.min(maxStriche, maxDashes, Math.round(overflow / pxJeStrich) + sockel));
        if (n !== dashCount) {
            dashesEl.innerHTML = '';
            const frag = document.createDocumentFragment();
            for (let i = 0; i < n; i++) {
                const d = document.createElement('span');
                d.className = 'scroll-dash';
                d.addEventListener('click', () => {
                    const ov = scrollEl.scrollHeight - scrollEl.clientHeight;
                    scrollEl.scrollTop = (i / (n - 1)) * ov;
                });
                frag.appendChild(d);
            }
            dashesEl.appendChild(frag);
            dashCount = n;
        }

        const progress = Math.min(1, Math.max(0, scrollEl.scrollTop / overflow));
        const active = Math.round(progress * (n - 1));
        const children = dashesEl.children;
        for (let i = 0; i < children.length; i++) {
            children[i].classList.toggle('is-active', i === active);
        }
    };

    scrollEl.addEventListener('scroll', update);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(update);
        ro.observe(scrollEl);
    }
    update();

    return () => {
        scrollEl.removeEventListener('scroll', update);
        if (ro) ro.disconnect();
    };
}
